import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, Clock, Gauge, Users, Loader2, BarChart3, Radio, ServerCog, Sparkles, MapPin, Map } from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SharedLinks } from "@/components/SharedLinks";
import { CountryViews } from "@/components/CountryViews";

const RANGES = [["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["0", "All time"]];

function StatCard({ icon: Icon, label, value, suffix }) {
  return (
    <div className="bg-card border border-gray-800 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
          <Icon className="h-5 w-5 text-amber-400" />
        </div>
      </div>
      <p className="mt-4 text-2xl font-display font-bold text-white">{value}<span className="text-base text-gray-500 font-normal ml-1">{suffix}</span></p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simOn, setSimOn] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [days, setDays] = useState("0");
  const [shares, setShares] = useState(null);

  const q = { params: days !== "0" ? { days } : {} };
  const loadAll = async () => {
    const [a, st, sh] = await Promise.all([
      api.get("/analytics", q),
      api.get("/analytics/simulate/status"),
      api.get("/analytics/shares", q),
    ]);
    setData(a.data);
    setSimOn(st.data.active);
    setShares(sh.data);
  };

  useEffect(() => {
    loadAll().catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, days]);

  const toggleSim = async (checked) => {
    setSimBusy(true);
    setSimOn(checked);
    try {
      if (checked) {
        const r = await api.post("/analytics/simulate");
        toast.success(`Generated ${r.data.generated} sample data points`);
      } else {
        await api.delete("/analytics/simulate");
        toast.success("Sample data cleared");
      }
      const a = await api.get("/analytics", q);
      setData(a.data);
    } catch (e) {
      toast.error("Failed to update sample data");
      setSimOn(!checked);
    } finally {
      setSimBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;
  }

  const s = data?.summary || {};
  const hasData = data?.has_data;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Analytics</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Viewer reach, watch time and bandwidth across your streams.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <select data-testid="analytics-range-select" value={days} onChange={(e) => setDays(e.target.value)}
          className="h-11 rounded-xl bg-gray-950 border border-gray-800 text-sm text-gray-200 px-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 cursor-pointer">
          {RANGES.map(([v, l]) => <option key={v} value={v} className="bg-gray-950">{l}</option>)}
        </select>
        <div data-testid="metrics-simulator" className="flex items-center gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-2.5">
          <Sparkles className="h-4 w-4 text-amber-400" />
          <div className="mr-1">
            <p className="text-sm font-medium text-amber-300 leading-none">Demo data</p>
            <p className="text-[11px] text-gray-500 mt-0.5">Preview charts with sample metrics</p>
          </div>
          {simBusy ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : (
            <Switch data-testid="simulator-toggle" checked={simOn} onCheckedChange={toggleSim} className="data-[state=checked]:bg-amber-500" />
          )}
        </div>
        </div>
      </div>

      {!hasData && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
          <ServerCog className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">Metrics pipeline is ready — pending media server</p>
            <p className="text-xs text-gray-400 mt-0.5">Charts and totals will populate automatically once your RTMP media server starts reporting viewer and bandwidth metrics.</p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        <StatCard icon={Eye} label="Total Views" value={s.total_views ?? 0} />
        <StatCard icon={Clock} label="Watch Time" value={s.watch_hours ?? 0} suffix="hrs" />
        <StatCard icon={Gauge} label="Bandwidth" value={s.bandwidth_gb ?? 0} suffix="GB" />
        <StatCard icon={Users} label="Peak Concurrent" value={s.peak_concurrent ?? 0} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2 mb-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-gray-800 rounded-2xl p-5">
          <h3 className="font-display text-base font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-amber-400" /> Views over time</h3>
          {hasData ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.timeseries}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.4} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 8, color: "#fff" }} />
                <Area type="monotone" dataKey="views" stroke="#F59E0B" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-card border border-gray-800 rounded-2xl p-5">
          <h3 className="font-display text-base font-semibold mb-4 flex items-center gap-2"><Gauge className="h-4 w-4 text-amber-400" /> Bandwidth (GB)</h3>
          {hasData ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 8, color: "#fff" }} />
                <Line type="monotone" dataKey="bandwidth_gb" stroke="#34D399" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </motion.div>
      </div>

      <div className="bg-card border border-gray-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800"><h3 className="font-display text-base font-semibold flex items-center gap-2"><Radio className="h-4 w-4 text-amber-400" /> Per-stream breakdown</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
              <th className="px-5 py-3 font-medium">Stream</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Views</th>
              <th className="px-5 py-3 font-medium">Watch (hrs)</th>
              <th className="px-5 py-3 font-medium">Bandwidth (GB)</th>
            </tr></thead>
            <tbody>
              {(data?.per_stream || []).length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-500">No streams yet.</td></tr>
              ) : data.per_stream.map((r) => (
                <tr key={r.stream_id} data-testid={`analytics-row-${r.stream_id}`} className="border-t border-gray-800/70 hover:bg-gray-800/30">
                  <td className="px-5 py-3 text-white font-medium">{r.title}</td>
                  <td className="px-5 py-3">
                    <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border " + (r.status === "active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/15 text-gray-400 border-gray-500/30")}>
                      <span className={"h-1.5 w-1.5 rounded-full " + (r.status === "active" ? "bg-emerald-400" : "bg-gray-500")} />
                      {r.status === "active" ? "Active" : "Offline"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-300">{r.views}</td>
                  <td className="px-5 py-3 text-gray-300">{r.watch_hours}</td>
                  <td className="px-5 py-3 text-gray-300">{r.bandwidth_gb}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SharedLinks data={shares} />

      <CountryViews items={data?.per_stream || []} days={days} />

      {/* Location breakdown */}
      <div className="grid gap-4 lg:grid-cols-2 mt-6">
        <div data-testid="views-by-state" className="bg-card border border-gray-800 rounded-2xl p-5">
          <h3 className="font-display text-base font-semibold mb-4 flex items-center gap-2"><Map className="h-4 w-4 text-amber-400" /> Views by State</h3>
          <LocationBars data={data?.by_state} labelKey="state" />
        </div>
        <div data-testid="views-by-city" className="bg-card border border-gray-800 rounded-2xl p-5">
          <h3 className="font-display text-base font-semibold mb-4 flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-400" /> Top Cities</h3>
          <LocationBars data={(data?.by_city || []).map((c) => ({ label: `${c.city}, ${c.state}`, views: c.views }))} labelKey="label" />
        </div>
      </div>
    </div>
  );
}

function LocationBars({ data, labelKey }) {
  const rows = (data || []).filter((r) => (r[labelKey] || "").toLowerCase() !== "unknown" || (data || []).length === 1);
  if (!rows || rows.length === 0) {
    return (
      <div className="h-[200px] flex flex-col items-center justify-center text-center border border-dashed border-gray-800 rounded-xl">
        <MapPin className="h-7 w-7 text-gray-700" />
        <p className="text-sm text-gray-500 mt-2">No location data yet</p>
        <p className="text-xs text-gray-600">Viewer cities appear here as people watch</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.views), 1);
  return (
    <div className="space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
      {rows.slice(0, 12).map((r, i) => (
        <div key={i} data-testid={`location-row-${i}`} className="flex items-center gap-3">
          <span className="w-40 shrink-0 text-sm text-gray-300 truncate">{r[labelKey]}</span>
          <div className="flex-1 h-6 bg-gray-800/60 rounded-md overflow-hidden">
            <div className="h-full gold-gradient rounded-md flex items-center justify-end pr-2" style={{ width: `${Math.max((r.views / max) * 100, 6)}%` }}>
              <span className="text-[11px] font-semibold text-black">{r.views}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[240px] flex flex-col items-center justify-center text-center border border-dashed border-gray-800 rounded-xl">
      <BarChart3 className="h-8 w-8 text-gray-700" />
      <p className="text-sm text-gray-500 mt-3">No data yet</p>
      <p className="text-xs text-gray-600">Awaiting metrics from the media server</p>
    </div>
  );
}
